/**
 * Sakin dashboard presenter'i
 */

import { AppState } from '../../modules/state.js';
import { MONTHS, MONTHS_SHORT, PRIORITY_LABELS } from '../../firebase-config.js';
import { formatDate, formatNumber } from '../../shared/utils/formatters.js';

interface ResidentDashboardPresenterDeps {
    escapeHtml: (value: string) => string;
    renderResidentRecentDecisions: () => void;
    initResidentCharts: () => void;
}

export function loadResidentProfilePresenter(): void {
    const apartmentNo = AppState.currentUser?.apartment;
    if (!apartmentNo) return;

    const apartment = (AppState.apartments || []).find(item => item.number === apartmentNo);
    if (!apartment) return;

    const nameEl = document.getElementById('profile-resident-name') as HTMLInputElement | null;
    const phoneEl = document.getElementById('profile-phone') as HTMLInputElement | null;
    const emailEl = document.getElementById('profile-email') as HTMLInputElement | null;
    const countEl = document.getElementById('profile-resident-count') as HTMLInputElement | null;
    const statusEl = document.getElementById('profile-status') as HTMLSelectElement | null;

    if (nameEl) nameEl.value = apartment.residentName || '';
    if (phoneEl) phoneEl.value = apartment.phone || '';
    if (emailEl) emailEl.value = apartment.email || '';
    if (countEl) countEl.value = String(apartment.residentCount || 0);

    if (statusEl) {
        const normalizedStatus = ['owner', 'tenant', 'empty'].includes(apartment.status)
            ? apartment.status
            : (apartment.status === 'occupied' ? 'tenant' : 'empty');
        statusEl.value = normalizedStatus;
    }
}

export function renderResidentDuesChartPresenter(year: number, apartmentNo: number): void {
    const timeline = document.getElementById('resident-dues-timeline');
    if (!timeline) return;

    const monthlyDue = AppState.settings.monthlyDueAmount || 0;
    let paidMonths = 0;
    let unpaidMonths = 0;
    let html = '';

    for (let month = 1; month <= 12; month++) {
        const isPaid = AppState.dues[year]?.[apartmentNo]?.[month] || false;
        const status = isPaid ? 'paid' : 'unpaid';

        if (isPaid) paidMonths++;
        else unpaidMonths++;

        html += `
            <div class="timeline-marker ${status}" title="${MONTHS[month - 1]}: ${isPaid ? 'Ödendi ✓' : 'Ödenmedi ✗'}">
                <div class="timeline-pin">
                    <div class="pin-body">${String(month).padStart(2, '0')}</div>
                    <div class="pin-tail"></div>
                </div>
                <div class="timeline-dot"></div>
                <span class="timeline-label">${MONTHS_SHORT[month - 1]}</span>
            </div>
        `;
    }

    timeline.innerHTML = html;

    const paidMonthsEl = document.getElementById('resident-paid-months');
    const unpaidMonthsEl = document.getElementById('resident-unpaid-months');
    const totalPaidEl = document.getElementById('resident-total-paid');
    if (paidMonthsEl) paidMonthsEl.textContent = String(paidMonths);
    if (unpaidMonthsEl) unpaidMonthsEl.textContent = String(unpaidMonths);
    if (totalPaidEl) totalPaidEl.textContent = `₺${formatNumber(paidMonths * monthlyDue)}`;
}

export function renderResidentMaintenancePresenter(deps: ResidentDashboardPresenterDeps): void {
    const container = document.getElementById('resident-maintenance-list');
    if (!container) return;

    const upcoming = AppState.maintenance
        .filter(item => item.status === 'pending')
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(0, 5);

    if (upcoming.length === 0) {
        container.innerHTML = '<p class="empty-state">Yaklaşan bakım yok</p>';
        return;
    }

    container.innerHTML = upcoming.map(item => `
        <div class="compact-item">
            <h4>🔧 ${deps.escapeHtml(item.title)} ${(Math.abs((Date.now() - new Date(item.date).getTime()) / 86400000) <= 14) ? '<span class="resident-item-badge">Yeni</span>' : ''}</h4>
            <p>${formatDate(item.date)}</p>
        </div>
    `).join('');
}

export function renderResidentTasksPresenter(deps: ResidentDashboardPresenterDeps): void {
    const container = document.getElementById('resident-tasks-list');
    if (!container) return;

    const active = AppState.tasks.filter(task => task.status !== 'completed').slice(0, 5);
    const statusIcons: Record<string, string> = { pending: '⏳', in_progress: '🔄' };

    if (active.length === 0) {
        container.innerHTML = '<p class="empty-state">Aktif iş yok</p>';
        return;
    }

    container.innerHTML = active.map(task => `
        <div class="compact-item" onclick="viewTaskDetail('${task.id}')">
            <h4>${statusIcons[task.status] || '📋'} ${deps.escapeHtml(task.title)} <span class="priority-badge ${task.priority}">${PRIORITY_LABELS[task.priority]}</span></h4>
            <p>${deps.escapeHtml(task.description)}</p>
        </div>
    `).join('');
}

export function updateResidentDashboardPresenter(deps: ResidentDashboardPresenterDeps): void {
    const apartmentNo = AppState.currentUser?.apartment;
    if (!apartmentNo) return;

    const apartment = AppState.apartments?.find(item => item.number === apartmentNo);
    const welcomeText = apartment?.residentName
        ? `Sn. ${apartment.residentName} (Daire ${apartmentNo})`
        : `Daire ${apartmentNo} - Apartman bilgileri ve durumu`;

    const welcomeEl = document.getElementById('resident-welcome-text');
    if (welcomeEl) welcomeEl.textContent = welcomeText;

    const mobileProfileName = document.getElementById('mobile-profile-name');
    const mobileProfileApartment = document.getElementById('mobile-profile-apartment');
    if (mobileProfileName) mobileProfileName.textContent = apartment?.residentName || 'Sakin';
    if (mobileProfileApartment) mobileProfileApartment.textContent = `Daire ${apartmentNo}`;

    const year = AppState.currentYear;
    const currentMonth = new Date().getMonth() + 1;
    const isPaidThisMonth = AppState.dues[year]?.[apartmentNo]?.[currentMonth] || false;

    const dueStatusEl = document.getElementById('resident-due-status');
    const yearEl = document.getElementById('resident-year');
    if (dueStatusEl) {
        dueStatusEl.innerHTML = isPaidThisMonth
            ? '<span class="status-badge paid">Bu Ay Ödendi ✓</span>'
            : '<span class="status-badge unpaid">Bu Ay Ödenmedi ✗</span>';
    }
    if (yearEl) yearEl.textContent = String(year);

    renderResidentDuesChartPresenter(year, apartmentNo);
    renderResidentMaintenancePresenter(deps);
    renderResidentTasksPresenter(deps);
    deps.renderResidentRecentDecisions();
    deps.initResidentCharts();
    loadResidentProfilePresenter();
}
